import dayjs from "dayjs";

// The window of a single day is per hour, of the longer periods per day (null)
export const DATE_FILTERS = [
  { value: "today", title: "Today", label: "today", window: "today" },
  { value: "yesterday", title: "Yesterday", label: "yesterday", window: "one_day" },
  { value: "last_7_days", title: "Last 7 days", label: "in the last 7 days", window: null },
  { value: "this_month", title: "This month", label: "this month", window: null },
  { value: "last_month", title: "Last month", label: "last month", window: null },
];

export const DEFAULT_DATE_FILTER = "last_7_days";

// First and last day of a date filter
const getDays = (value, today) => {
  switch (value) {
    case "today":
      return [today, today];
    case "yesterday":
      return [today.subtract(1, "day"), today.subtract(1, "day")];
    case "this_month":
      return [today.startOf("month"), today];
    case "last_month": {
      const lastMonth = today.subtract(1, "month");
      return [lastMonth.startOf("month"), lastMonth.endOf("month")];
    }
    default:
      return [today.subtract(6, "day"), today];
  }
};

/**
 * Period of the date filter
 *
 * @param {string} value - Value of the date filter, see DATE_FILTERS
 * @param {dayjs.Dayjs} [today] - Current day
 * @returns {object} First and last day, the label of the filter and the window (per hour for a single day, null per
 *   day)
 */
export const getPeriod = (value, today = dayjs()) => {
  const filter =
    DATE_FILTERS.find((option) => option.value === value) ||
    DATE_FILTERS.find((option) => option.value === DEFAULT_DATE_FILTER);
  const [start, end] = getDays(filter.value, today);

  return { start: start.startOf("day"), end: end.startOf("day"), window: filter.window, label: filter.label };
};

// URL parameters that open a dashboard ("View more") on the period of the homepage
export const getDashboardDates = (period) =>
  `start=${period.start.format("YYYY-MM-DD")}&end=${period.end.format("YYYY-MM-DD")}`;

export const sumBy = (rows, field) => (rows || []).reduce((total, row) => total + (row[field] || 0), 0);

// Per event and hour or day ("purchase_2026-11-01"), how the DataLayer Guard timelines are merged
const sumPerEventAndDate = (rows, field) => {
  const totals = new Map();
  (rows || []).forEach((row) => {
    const key = `${row.event_name}_${row.date}`;
    totals.set(key, (totals.get(key) || 0) + (row[field] || 0));
  });
  return totals;
};

/**
 * Events and error events of DataLayer Guard, for its error rate and the successfully monitored events
 *
 * The errors come from the errors table and the events from the snapshots, and not every event with an error has a
 * snapshot (e.g. the network checks). An event with an error was checked too, so per event and hour or day there are
 * at least as many events as errors: the rate can't go over 100%. Without any snapshots the event data is missing,
 * so the events stay 0 and the gauge shows the data as incomplete instead of 100%.
 *
 * @param {object[]} [errorsTimeline] - Rows with date, event_name and error_count
 * @param {object[]} [totalEventsTimeline] - Rows with date, event_name and total_events (the snapshots)
 * @returns {{events: number, errors: number}} Checked events and events with errors of the period
 */
export const getDataLayerGuardTotals = (errorsTimeline, totalEventsTimeline) => {
  const errorsPerKey = sumPerEventAndDate(errorsTimeline, "error_count");
  const eventsPerKey = sumPerEventAndDate(totalEventsTimeline, "total_events");
  const errors = sumBy(errorsTimeline, "error_count");
  if (sumBy(totalEventsTimeline, "total_events") === 0) return { events: 0, errors };

  let events = 0;
  new Set([...eventsPerKey.keys(), ...errorsPerKey.keys()]).forEach((key) => {
    events += Math.max(eventsPerKey.get(key) || 0, errorsPerKey.get(key) || 0);
  });
  return { events, errors };
};

/**
 * Error rate of a gauge, with the reason when there is no rate
 *
 * @param {object|null|undefined} context - Response the totals come from, undefined while loading and null when the
 *   request failed
 * @param {number} fails - Number of errors
 * @param {number} total - Number of events
 * @returns {{value: number|null, status: string}} Unrounded percentage and "ok", or no value and "loading",
 *   "failed", "incomplete" (errors without events) or "empty"
 */
export const getErrorRate = (context, fails, total) => {
  if (context === undefined) return { value: null, status: "loading" };
  if (context === null) return { value: null, status: "failed" };
  if (total > 0) return { value: (fails / total) * 100, status: "ok" };
  // Errors without events means the event data is missing, so it must not look like "no data"
  return { value: null, status: fails > 0 ? "incomplete" : "empty" };
};

// Today, for the requests that ignore the date filter (the same period, so they share one request)
export const getTodayPeriod = () => {
  const today = dayjs().startOf("day");
  return { start: today, end: today, window: null };
};

// Dutch notation like the Tag Monitor dashboard: 100.000 instead of 100000 (or 100,000 in an English browser)
export const NUMBER_LOCALE = "nl-NL";
const NUMBER_FORMAT = new Intl.NumberFormat(NUMBER_LOCALE);

// "100.000"
export const formatNumber = (number) => NUMBER_FORMAT.format(number);

// "45 min", "14 h" or "3 days"
export const formatMinutes = (minutes) => {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h`;
  return `${Math.round(minutes / (24 * 60))} days`;
};
